import React, { FC, useState } from 'react';
import { Accordion } from '@components/UIKit/Accordion/Accordion';
import { Header } from '@components/Pages/ToyPage/AccordionItems/Header';
import { Body } from '@components/Pages/ToyPage/AccordionItems/Body';
import { useTranslation } from '@hooks/useTranslation';
import s from './artistSection.module.scss';
import { ArtistAvatar } from '@components/UIKit/ArtistAvatar/ArtistAvatar';
import cn from 'classnames';
import { LinksEnum } from '@specs/navigation';
import Link from 'next/link';
import { CollaboratorsType } from '@services/social/types';
import { FollowButton } from '@components/FollowButton/FollowButton';
import { ExpandableText } from '@components/UIKit/ExpandableText/ExpandableText';
import { useCountFollowers } from '@hooks/TRPC_Hooks/useCountFollowers';
import { useArtistToyCount } from '@hooks/TRPC_Hooks/useArtistToyCount';
import { NumberLoader } from '@components/NumberLoader/NumberLoader';
import { useGetIsBlocked } from '@hooks/TRPC_Hooks/useGetIsBlocked';
interface Props {
  artists: CollaboratorsType;
}
const ARTIST_DESCRIPTION_DESKTOP_MAX_LENGTH = 230;
const ArtistSectionItem = ({
  artist,
  isOpen
}: {
  artist: CollaboratorsType[0];
  isOpen: boolean;
}) => {
  const {
    translate: tr
  } = useTranslation();
  const {
    id,
    profile
  } = artist;
  const {
    data: followers,
    isLoading: isLoadingFollowers
  } = useCountFollowers({
    shortid: artist?.profile?.shortid ?? ''
  }, {
    enabled: isOpen && typeof artist?.profile?.shortid === 'string'
  });
  const {
    data: count,
    isLoading: countLoading
  } = useArtistToyCount({
    artistId: artist?.id ?? -1
  }, {
    enabled: typeof artist?.id === 'number'
  });
  const {
    isBlocked
  } = useGetIsBlocked({
    targetShortId: artist?.profile?.shortid ?? ''
  });
  const getArtistName = ({
    shortid
  }: CollaboratorsType[0]['profile']) => shortid || '';
  const getArtistDescription = ({
    description
  }: CollaboratorsType[0]['profile']) => description || '';
  return <div className={s.artistSection} key={id} data-sentry-component="ArtistSectionItem" data-sentry-source-file="ArtistSection.tsx">
            <div className={s.artistSection__row}>
                <div className={s.artistSection__row__left}>
                    <div className={s.artistSection__avatar}>
                        <ArtistAvatar srcSet={[{
            width: 60,
            height: 60,
            minMedia: 0
          }]} shortId={getArtistName(profile)} data-sentry-element="ArtistAvatar" data-sentry-source-file="ArtistSection.tsx" />
                    </div>

                    <div>
                        <Link className={cn(s.artistSection__name, '_ellipsis')} href={{
            pathname: LinksEnum.USER,
            query: {
              slug: getArtistName(profile)
            }
          }} data-sentry-element="Link" data-sentry-source-file="ArtistSection.tsx">
                            {getArtistName(profile)}
                        </Link>

                        <div className={s.artistSection__stats}>
                            <div className={s.statsItem__value}>{isLoadingFollowers ? <NumberLoader digitCount={3} /> : followers}</div>
                            <div className={s.statsItem__text}>{tr({
                id: 'toy.page.artist.stats.followers'
              })}</div>
                            |
                            <div className={s.statsItem__value}>{countLoading ? <NumberLoader digitCount={3} /> : count}</div>
                            <div className={s.statsItem__text}>{tr({
                id: 'toy.page.artist.stats.toys'
              })}</div>
                        </div>
                    </div>

                </div>

                {!isBlocked && <FollowButton shortid={getArtistName(profile)} buttonProps={{
        color: 'Black',
        size: 'small'
      }} />}
            </div>
            <ExpandableText buttonProps={{
      size: 'extra_small'
    }} maxLength={ARTIST_DESCRIPTION_DESKTOP_MAX_LENGTH} boxClassName={s.artistSection__description} text={getArtistDescription(profile)} data-sentry-element="ExpandableText" data-sentry-source-file="ArtistSection.tsx" />
        </div>;
};
export const ArtistSection: FC<Props> = ({
  artists
}) => {
  const {
    translate: tr
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  return <Accordion toggler={<Header onClick={() => {
    setIsOpen(prev => !prev);
  }} isOpen={isOpen} isDisabled={!artists.length}>
                    {tr({
      id: 'toy.page.artist.title'
    })}
                </Header>} isOpen={isOpen} data-sentry-element="Accordion" data-sentry-component="ArtistSection" data-sentry-source-file="ArtistSection.tsx">
            <Body data-sentry-element="Body" data-sentry-source-file="ArtistSection.tsx">
                {artists.length && <div className={s.artistSection__list}>
                        {artists.map((artist, i) => <>
                                <ArtistSectionItem isOpen={isOpen} artist={artist} />
                                {i !== artists.length - 1 && <div className={s.artistSection__divider} />}
                            </>)}
                    </div>}
            </Body>
        </Accordion>;
};